<template>
  <v-layout
    align-center
    align-content-center
    fill-height
    fluid
    justify-center
    class='reg-video-layout'
    v-resize='onResize'
  >
    <div :style='blockStyle' ref='videoContainer'>
      <video
        ref='video'
        autoplay
        :muted='muted'
        :src='contentUrl'
        @loadstart='onLoadStart'
        @pause='onPause'
        @durationchange='onDurationChange'
        @volumechange='onVolumeChange'
        @play='onPlay'
        @canplay='onCanPlay'
        @timeupdate='onTimeUpdate'
        @seeked='onSeeked'
        @loadedmetadata='onMetadataLoaded'
        class='reg-video-element'
      />
      <my-hotspot
        v-for='(hs, index) in filteredHotspots'
        :key='index'
        :hs='hs'
        :ptop='vtop()'
        :pleft='vleft()'
        :pwidth='vwidth()'
        :pheight='vheight()'
        :isVideo='true'
      />
      <my-controls v-if='videoLoaded' class='reg-video-controls'/>
    </div>

  </v-layout>
</template>

<script>
import { Logger } from 'aws-amplify';
import { MediaPlayer } from 'dashjs';
import Hotspot from './Hotspot.vue';
import VideoControls from '@/components/controls/VideoControls.vue';
import EventBus from '@/EventBus';

const logger = new Logger('RegularVideo');

export default {
  data() {
    return {
      left: 0,
      top: 0,
      width: 0,
      height: 0,
      playing: false,
      filtered: [],
      videoLoaded: false,
      hotspots: null,
      dashPlayer: null,
      videoElWidth: 0,
      videoElHeight: 0,
    };
  },
  components: {
    'my-hotspot': Hotspot,
    'my-controls': VideoControls,
  },
  created() {
    this.$eventHub.$on('video-event', this.videoEvents);
    document.addEventListener('visibilitychange', this.onResize, false);
  },
  beforeDestroy() {
    this.$eventHub.$off('video-event');
  },
  mounted() {
    console.log(`player.2D v${process.env.VUE_APP_2DPLAYER_VERSION}`);
    this.hotspots = this.$store.state.hotspots;
    const { contentUrl } = this;
    // Create dash player if url is dash playlist
    if (contentUrl.includes('.mpd')) {
      this.dashPlayer = MediaPlayer().create();
      this.dashPlayer.initialize(this.$refs.video, contentUrl, true);
    }
    this.onResize();
  },
  computed: {
    muted() {
      return this.$store.state.muted;
    },
    contentUrl() {
      const { startTime } = this.$store.state;
      return `${this.$store.state.regularVideoUrl}#t=${startTime}`;
    },
    filteredHotspots() {
      return this.filtered;
    },
    blockStyle() {
      return {
        display: 'flex',
        width: `${this.videoElWidth}px`,
        height: `${this.videoElHeight}px`,
        position: 'fixed',
      };
    },
  },
  methods: {
    vtop() {
      const { videoContainer } = this.$refs;
      return Number(window.getComputedStyle(videoContainer).getPropertyValue('top').slice(0, -2));
    },
    vleft() {
      const { videoContainer } = this.$refs;
      return Number(window.getComputedStyle(videoContainer).getPropertyValue('left').slice(0, -2));
    },
    vheight() {
      const { video } = this.$refs;
      return Number(window.getComputedStyle(video).getPropertyValue('height').slice(0, -2));
    },
    vwidth() {
      const { video } = this.$refs;
      return Number(window.getComputedStyle(video).getPropertyValue('width').slice(0, -2));
    },
    videoEvents(evt) {
      switch (evt.type) {
        case 'unMute':
          this.$refs.video.muted = false;
          break;
        case 'volume':
          this.$refs.video.volume = evt.volume;
          break;
        case 'seek':
          this.realSeek = true;
          this.$refs.video.currentTime = evt.time;
          break;
        case 'play':
          this.$refs.video.play();
          break;
        case 'pause':
          this.$refs.video.pause();
          break;
        case 'loop':
          this.$refs.video.loop = !this.$refs.video.loop;
          this.$store.commit('loopEnabled', this.$refs.video.loop);
          break;
        case 'toggleMute':
          this.$refs.video.muted = !this.$refs.video.muted;
          break;
        case 'changeVideoSource':
          this.changeVideo(evt.src);
          break;
        case 'setAutoQuality':
          this.setAutoQuality();
          break;
        default:
      }
    },
    onMetadataLoaded(evt) {
      const { videoWidth, videoHeight } = evt.target;
      this.videoWidth = videoWidth;
      this.videoHeight = videoHeight;
    },
    onCanPlay() {
      logger.debug('RegularVideo.onCanPlay');
      this.videoLoaded = true;
      this.onResize();
      this.$store.commit('appState', 'CANPLAY');
    },
    onPause() {
      this.$store.commit('playState', 0);
      this.$store.commit('appState', 'PAUSED');
    },
    onPlay() {
      this.$store.commit('playState', 1);
      this.$store.commit('playbackStarted', true);
      this.$store.commit('appState', 'PLAYING');
    },
    onLoadStart() {
      const { volume } = this.$store.state;
      logger.debug(`RegularVideo.onLoadStart ${volume}`);
      this.$refs.video.volume = volume;
      // this.$refs.video.muted = volume === 0 ? true : false;
    },
    onDurationChange(evt) {
      this.$store.commit('duration', evt.target.duration);
    },
    onVolumeChange(evt) {
      logger.debug(`RegularVideo.onVolumeChange ${evt.target.volume}`);
      this.$store.commit('volume', evt.target.volume);
      this.$store.commit('muted', evt.target.muted);
    },
    onSeeked() {
      this.hotspots.forEach(hs => hs.completed = false);
      if (this.realSeek) {
        this.hotspots.forEach(hs => hs.showThis = false);
        this.realSeek = !this.realSeek;
      }
    },
    computedHotspots() {
      const { hotspots } = this.$store.state;
      const hss = hotspots.map((hs) => {
        hs.timeStamp = hs.timeStamp || 0;
        hs.duration = hs.hasDuration ? hs.duration : 0.5;
        hs.pausesPlayBack = hs.pausesPlayBack || false;
        return hs;
      });
      return hss;
    },
    onTimeUpdate(evt) {
      this.currentTime = evt.target.currentTime;
      this.filterHotspots();
      this.$store.commit('currentTime', evt.target.currentTime);
    },
    play() {
      if (this.playing) this.$refs.video.pause();
      else this.$refs.video.play();
      this.playing = !this.playing;
    },
    pause() {
      this.$refs.video.pause();
    },
    onResize() {
      const { video, videoContainer } = this.$refs;
      this.top = Number(window.getComputedStyle(videoContainer).getPropertyValue('top').slice(0, -2));
      this.left = Number(window.getComputedStyle(videoContainer).getPropertyValue('left').slice(0, -2));
      this.width = Number(window.getComputedStyle(video).getPropertyValue('width').slice(0, -2));
      this.height = Number(window.getComputedStyle(video).getPropertyValue('height').slice(0, -2));

      EventBus.$emit('resized', {
        top: this.top,
        left: this.left,
        width: this.width,
        height: this.height,
      });

      // Calculate width & height for video element
      const videoAspectRatio = this.videoWidth / this.videoHeight;
      const windowAspectRatio = window.innerWidth / window.innerHeight;
      if (videoAspectRatio > windowAspectRatio) {
        this.videoElWidth = window.innerWidth;
        this.videoElHeight = this.videoElWidth / videoAspectRatio;
      } else {
        this.videoElHeight = window.innerHeight;
        this.videoElWidth = videoAspectRatio * this.videoElHeight;
      }
    },
    filterHotspots() {
      const { hotspots } = this;
      this.filtered = hotspots.filter((hs) => {
        if (this.currentTime > hs.timeStamp
        && this.currentTime < (hs.timeStamp + hs.duration)) {
          if (hs.pausesPlayBack && (!hs.completed || typeof hs.completed === 'undefined')) {
            // hs.completed = !hs.completed;
            if (hs.showThis) {
              hs.showThis = !hs.showThis;
              return false;
            }
            this.pause();
            this.$refs.video.currentTime = hs.timeStamp;
            hs.showThis = true;
          }
          return true;
        } else if (hs.showThis && !this.realSeek) {
          return true;
        }
        return false;
      });
    },
    changeVideo(url) {
      if (this.dashPlayer) {
        this.dashPlayer.reset();
      }
      this.$refs.video.removeAttribute('src');
      this.$refs.video.load();
      this.$refs.video.src = `${url}#t=${this.currentTime}`;
      this.$refs.video.play();
    },
    setAutoQuality() {
      const { dash } = this.$store.state.project;
      if (!this.dashPlayer) {
        this.dashPlayer = MediaPlayer().create();
        this.dashPlayer.initialize(this.$refs.video, `${dash}#t=${this.currentTime}`, true);
      } else {
        this.dashPlayer.attachView(this.$refs.video);
        this.dashPlayer.attachSource(`${dash}#t=${this.currentTime}`);
      }
    },
  },
};
</script>

<style scoped>
.reg-video-layout {
    background-color:rgba(220, 220, 220, 1);
}

.reg-video-controls {
  position: absolute;
  height: 50px;
  bottom: 10px;
  left: 10px;
  right: 10px;
}

.reg-video-element {
  width: 100%;
  height: 100% !important;
  position: absolute !important;
  bottom: 0;
  top:0;
  left: 0;
  right: 0;

}
</style>
